import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { debounce } from '@prospective/lithium';

import type { ClientError } from '@/api/client';
import translator from '@/locale/translator';
import type { RegistrationDraft, RegistrationStateResponse, RegistrationStepMarker } from '@/types/registration';
import type { SSOIdentity } from '@/types/sso';
import {
  clearFeatureSetupWarning,
  clearPersistedRegistration,
  readFeatureSetupWarning,
  readPersistedRegistration,
  writeFeatureSetupWarning,
  writePersistedRegistration,
} from '@/composables/useRegistrationPersistence';

/** an `onContinue` result that advances to `goTo` instead of the next step */
export interface RegistrationStepJump {
  goTo: RegistrationStepKey;
}

export interface RegistrationStepState {
  /** whether the wizard may advance past this step; the step component owns the flag */
  canGoNext: boolean;
  /**
   * runs before advancing; returning false or throwing keeps the registrant on the step, so a lost connection never looks
   * like a save. Returning `{ goTo }` advances to that step instead of the next one.
   */
  onContinue?: () => boolean | RegistrationStepJump | Promise<boolean | RegistrationStepJump>;
  /** tells the backend the step was skipped; same failure rules as `onContinue` */
  onSkip?: () => Promise<void>;
}

export const registrationStepKeys = [
  'account',
  'verify_email',
  'profile',
  'company',
  'goal',
  'package',
  'done',
] as const;

export type RegistrationStepKey = (typeof registrationStepKeys)[number];

/** the completion screen is listed in the sidebar but not counted by the breadcrumb */
const COMPLETION_STEP: RegistrationStepKey = 'done';

/** the goal step is the only one a registrant may pass without answering */
const SKIPPABLE_STEPS: RegistrationStepKey[] = ['goal'];

/** steps with no reassuring line under the footer simply have no note key */
const STEPS_WITH_NOTES: RegistrationStepKey[] = ['account', 'verify_email', 'company', 'goal', 'package'];

/** steps with no back action: the profile step is the first after the account exists, whose details can no longer change */
const NO_BACK_STEPS: ReadonlySet<RegistrationStepKey> = new Set(['profile']);

/** account setup steps; the sidebar stops offering them once the account exists */
const ACCOUNT_SETUP_STEPS: ReadonlySet<RegistrationStepKey> = new Set(['account', 'verify_email']);

/** the sequence at which the account exists, past which the account setup steps lock */
const PROFILE_STEP_SEQUENCE = registrationStepKeys.indexOf('profile') + 1;

function emptyDraft(): RegistrationDraft {
  return {
    account: { email: '', mobile: '' },
    profile: { firstName: '', lastName: '', jobTitle: '', preferredLanguage: '' },
    company: {
      name: '',
      careersPage: '',
      industry: '',
      companySize: '',
      street: '',
      postcode: '',
      city: '',
      uid: '',
      invoiceEmail: '',
      billingSameAsCompany: true,
      billingStreet: '',
      billingPostcode: '',
      billingCity: '',
      logoId: null,
      brandColors: [],
    },
    goal: { mainGoal: '', rolesPerYear: '', whenToStart: '' },
    package: { campaignMode: '' },
  };
}

function defaultStepStates(): Record<RegistrationStepKey, RegistrationStepState> {
  return registrationStepKeys.reduce(
    (states, key) => {
      states[key] = { canGoNext: false };
      return states;
    },
    {} as Record<RegistrationStepKey, RegistrationStepState>
  );
}

export const useRegistrationStore = defineStore('registration', () => {
  /* ---------------------------------- STATE ---------------------------------- */
  const draft = ref<RegistrationDraft>(emptyDraft());
  const availableSteps = ref(defaultStepStates());
  const currentStepSequence = ref(1);
  // the sidebar reveals one step ahead and never un-reveals
  const revealedStepCount = ref(2);
  /** true while a step's continue handler is in flight, so the action cannot be fired twice */
  const isSubmitting = ref(false);
  /**
   * Set once the email code is accepted (which also creates the session). Not persisted: a stored flag can
   * outlive an expired session and restore into steps whose requests will fail, so it is read back from the session.
   */
  const emailVerified = ref(false);
  /** whether the mobile was confirmed; persisted so a language-switch reload doesn't re-prompt, but the session outranks it (see rehydrate) */
  const mobileVerified = ref(false);
  /**
   * What the server said when the code was sent: expiry, resend cooldown and masked mobile. Not persisted, so a reload
   * restarts the countdown (the server enforces the real cooldown). Expiry defaults to ten minutes until the first response.
   */
  const verification = ref({ maskedMobile: '', expiresInSeconds: 600, resendAfterSeconds: 0 });
  /** set when a submission is refused because the registration is already finished elsewhere; see `next()` */
  const finishedElsewhere = ref(false);
  /**
   * Set by the package step when a default customer feature failed to configure automatically.
   * Carried forward to the completion screen, so it must survive the step change - written
   * through `setFeatureSetupWarning()` to its own storage key (see `useRegistrationPersistence`).
   */
  const featureSetupWarning = ref(false);
  /**
   * The step ceiling reported by the account's step marker: `undefined` means no session has reported one,
   * `null` means finished. Set in `rehydrate()` and refreshed by each step's `applyServerState()`; once set it drives
   * `furthestRestorableSequence`.
   */
  const registrationStepCeiling = ref<RegistrationStepMarker | null | undefined>(undefined);
  /**
   * The identity a Google or Microsoft sign-in verified, while the account step is confirming it. Not persisted: the
   * session holds it and the account step reads it back from there after a reload.
   */
  const ssoIdentity = ref<SSOIdentity | null>(null);

  /* -------------------------------- COMPUTED --------------------------------- */
  const steps = computed(() =>
    registrationStepKeys.map((key, index) => ({
      key,
      sequence: index + 1,
      section: availableSteps.value[key],
      /** whether this step's footer shows a back action */
      offersBack: index > 0 && !NO_BACK_STEPS.has(key),
    }))
  );

  /** the steps the breadcrumb counts, which excludes the completion screen */
  const inputStepCount = computed(() => steps.value.length - 1);

  const currentStep = computed(() => steps.value[currentStepSequence.value - 1] ?? steps.value[0]);

  const isComplete = computed(() => currentStep.value.key === COMPLETION_STEP);

  function stepLabel(key: RegistrationStepKey) {
    return translator.get(`registration.steps.${key}`);
  }

  const breadcrumb = computed(() => {
    if (isComplete.value) return translator.get('registration.breadcrumb.complete');

    return translator.get('registration.breadcrumb.step', {
      current: currentStepSequence.value,
      total: inputStepCount.value,
      label: stepLabel(currentStep.value.key),
    });
  });

  const isLastInputStep = computed(() => currentStepSequence.value === inputStepCount.value);

  const nextLabel = computed(() => {
    if (currentStepSequence.value === 1) return translator.get('registration.footer.create_account');
    if (isLastInputStep.value) return translator.get('registration.footer.finish_setup');
    return translator.get('registration.footer.continue');
  });

  const footerNote = computed(() => {
    if (isComplete.value || !STEPS_WITH_NOTES.includes(currentStep.value.key)) return '';
    return translator.get(`registration.notes.${currentStep.value.key}`);
  });

  const showFooter = computed(() => !isComplete.value && !finishedElsewhere.value);

  /** back is offered per step via `offersBack`; none on the profile step since the account's details can't change */
  const canGoBack = computed(() => currentStep.value.offersBack && !isComplete.value);

  const canSkip = computed(() => SKIPPABLE_STEPS.includes(currentStep.value.key));

  const canGoNext = computed(() => currentStep.value.section.canGoNext);

  /** measured against the input steps, so the last one before completion reads as full */
  const progress = computed(() => {
    if (isComplete.value) return 100;
    return (currentStepSequence.value / inputStepCount.value) * 100;
  });

  /**
   * How far a restored position (url or storage) may reach, checked against the draft so a link to `?step=package` can't
   * skip ahead. Once a session reports its ceiling (`registrationStepCeiling`) that replaces the heuristics; `null`
   * restores the full length and the router guard leaves finished registrations for the dashboard.
   */
  const furthestRestorableSequence = computed(() => {
    if (registrationStepCeiling.value !== undefined) {
      if (registrationStepCeiling.value === null) return steps.value.length;
      const index = registrationStepKeys.indexOf(registrationStepCeiling.value);
      const ceiling = index === -1 ? steps.value.length : index + 1;
      // the backend refuses every step past the profile until the mobile is confirmed
      return mobileVerified.value ? ceiling : Math.min(ceiling, PROFILE_STEP_SEQUENCE);
    }
    if (emailVerified.value) return steps.value.length;
    if (draft.value.account.email) return 2;
    return 1;
  });

  /** the sidebar lists revealed steps only, with the current one marked */
  const sidebarSteps = computed(() =>
    steps.value.slice(0, isComplete.value ? steps.value.length : revealedStepCount.value).map((step) => {
      // clickable once passed; account setup steps only until the account exists
      const isClickable =
        !isComplete.value &&
        step.sequence < currentStepSequence.value &&
        !(ACCOUNT_SETUP_STEPS.has(step.key) && currentStepSequence.value >= PROFILE_STEP_SEQUENCE);

      return {
        ...step,
        label: stepLabel(step.key),
        isCurrent: !isComplete.value && step.sequence === currentStepSequence.value,
        isDone: isComplete.value || step.sequence < currentStepSequence.value,
        isClickable,
      };
    })
  );

  /* --------------------------------- METHODS --------------------------------- */
  function goToStep(sequence: number) {
    const clamped = Math.min(Math.max(sequence, 1), steps.value.length);
    currentStepSequence.value = clamped;
    // reveals one ahead so the completion entry is visible from the last step
    revealedStepCount.value = Math.max(revealedStepCount.value, Math.min(clamped + 1, steps.value.length));
  }

  async function next() {
    if (isSubmitting.value) return;

    const handler = currentStep.value.section.onContinue;
    if (!handler) {
      goToStep(currentStepSequence.value + 1);
      return;
    }

    isSubmitting.value = true;

    try {
      const result = await handler();
      if (result === false) return;

      goToStep(result === true ? currentStepSequence.value + 1 : registrationStepKeys.indexOf(result.goTo) + 1);
    } catch (error) {
      // the shared client already surfaces the message; only don't advance, except when the client's position is out of
      // sync with the backend's, where retrying would just refuse again
      recoverFromUnreachableStep(error);
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * The backend refused a submission the client's position disagrees with: either walk back to the real step,
   * or, if the registration is already finished elsewhere (second tab, stale reload), leave the wizard.
   */
  function recoverFromUnreachableStep(error: unknown) {
    const body = (error as ClientError)?.responseJson;

    if (body?.code === 'registration_mobile_unverified') {
      mobileVerified.value = false;
      goToStep(PROFILE_STEP_SEQUENCE);
      return;
    }

    if (body?.code !== 'registration_step_unreachable') return;

    if (body.registration_step) {
      const target = resolveRequestedStep(body.registration_step);
      if (target) goToStep(target.sequence);
      return;
    }

    finishedElsewhere.value = true;
  }

  function back() {
    goToStep(currentStepSequence.value - 1);
  }

  /** skipping advances without the step being valid; `onSkip` lets the backend advance its own step marker first */
  async function skip() {
    if (!canSkip.value || isSubmitting.value) return;

    isSubmitting.value = true;

    try {
      await currentStep.value.section.onSkip?.();
      goToStep(currentStepSequence.value + 1);
    } catch (error) {
      recoverFromUnreachableStep(error);
    } finally {
      isSubmitting.value = false;
    }
  }

  function resolveRequestedStep(stepQuery: string | null | undefined) {
    if (!stepQuery) return undefined;
    return steps.value.find((step) => step.key === stepQuery);
  }

  function restoreStepFromQuery(stepQuery: string | null | undefined) {
    const step = resolveRequestedStep(stepQuery);
    if (!step) return undefined;

    // an unreachable request lands on the furthest supported step
    if (step.sequence > furthestRestorableSequence.value) {
      goToStep(furthestRestorableSequence.value);
      return undefined;
    }

    goToStep(step.sequence);
    return step;
  }

  function setVerification(next: Partial<typeof verification.value>) {
    verification.value = { ...verification.value, ...next };
  }

  /** also writes through to storage, so a reload before the completion screen is read does not lose it */
  function setFeatureSetupWarning(hasWarning: boolean) {
    featureSetupWarning.value = hasWarning;
    writeFeatureSetupWarning(hasWarning);
  }

  function clearSSOIdentity() {
    ssoIdentity.value = null;
  }

  function resetState() {
    draft.value = emptyDraft();
    availableSteps.value = defaultStepStates();
    currentStepSequence.value = 1;
    isSubmitting.value = false;
    revealedStepCount.value = 2;
    emailVerified.value = false;
    mobileVerified.value = false;
    verification.value = { maskedMobile: '', expiresInSeconds: 600, resendAfterSeconds: 0 };
    finishedElsewhere.value = false;
    featureSetupWarning.value = false;
    clearFeatureSetupWarning();
    registrationStepCeiling.value = undefined;
    clearSSOIdentity();
    clearPersistedRegistration();
  }

  /* ------------------------------- PERSISTENCE ------------------------------- */
  /**
   * Mirrors progress into session storage so a reload doesn't lose it. Watched deeply rather than via blur handlers,
   * which miss typing in the last field before a reload and never fire for selects, checkboxes or pickers.
   */
  function startPersisting() {
    const persist = debounce(() => {
      // reaching completion schedules its own write, which would land after storage was cleared and restore the draft;
      // bail rather than cancel, since the debounce helper has no cancel
      if (isComplete.value) return;

      writePersistedRegistration(draft.value, currentStep.value.key, emailVerified.value, mobileVerified.value);
    }, 400);

    watch([draft, currentStep, mobileVerified], persist, { deep: true });

    // cleared outside the debounce so closing the tab right away doesn't leave the answers in storage
    watch(isComplete, (complete) => {
      if (complete) clearPersistedRegistration();
    });
  }

  /**
   * @param isAuthenticated whether the session says the registrant is signed in; passed in to keep this store free of the user store.
   * @param mobileVerifiedFromServer the backend's durable mobile-confirmed record; it outranks the stored flag.
   * @param registrationStepFromServer the current-user response's step marker; sets the ceiling that clamps a restored position.
   *
   * The stored flags only matter without a session (language-switch reload, mock flow), where they keep an already-verified
   * registrant off the verification step.
   */
  function rehydrate(
    isAuthenticated = false,
    mobileVerifiedFromServer: boolean | null = null,
    registrationStepFromServer?: RegistrationStepMarker | null
  ) {
    const persisted = readPersistedRegistration();

    // set first, since how far a position may be restored depends on it
    emailVerified.value = isAuthenticated || persisted?.emailVerified === true;
    mobileVerified.value = isAuthenticated ? mobileVerifiedFromServer === true : persisted?.mobileVerified === true;
    if (isAuthenticated) registrationStepCeiling.value = registrationStepFromServer ?? null;

    // its own storage key, independent of the draft below - see setFeatureSetupWarning
    featureSetupWarning.value = readFeatureSetupWarning();

    if (!persisted) return;

    draft.value = { ...emptyDraft(), ...persisted.draft };
    restoreStepFromQuery(persisted.step);
  }

  /**
   * Applies the global parts of a fresh `GET /v1/account/registration` (verification flags, step ceiling, account email,
   * masked mobile), called by `useRegistrationStepSync` on every step entry so a second tab or device's progress is picked up.
   *
   * The account email seeds the company step's invoice email default. The mobile is left alone: the server only reports a
   * masked number, which would make correcting a typo impossible in the edit field.
   */
  function applyServerState(state: RegistrationStateResponse) {
    emailVerified.value = state.emailVerified;
    mobileVerified.value = state.mobileVerified;
    registrationStepCeiling.value = state.step;
    if (state.maskedMobile) setVerification({ maskedMobile: state.maskedMobile });
    if (state.email) draft.value.account.email = state.email;
  }

  return {
    draft,
    availableSteps,
    currentStepSequence,
    revealedStepCount,
    isSubmitting,
    emailVerified,
    mobileVerified,
    verification,
    finishedElsewhere,
    featureSetupWarning,
    registrationStepCeiling,
    ssoIdentity,
    steps,
    inputStepCount,
    currentStep,
    isComplete,
    breadcrumb,
    isLastInputStep,
    nextLabel,
    footerNote,
    showFooter,
    canGoBack,
    canSkip,
    canGoNext,
    progress,
    furthestRestorableSequence,
    sidebarSteps,
    stepLabel,
    goToStep,
    next,
    back,
    skip,
    resolveRequestedStep,
    restoreStepFromQuery,
    setVerification,
    setFeatureSetupWarning,
    clearSSOIdentity,
    resetState,
    startPersisting,
    rehydrate,
    applyServerState,
  };
});
